(() => {
  const root = document.querySelector('[data-akd-print-config]');

  if (!root || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  const apiBase = root.dataset.apiBase;
  const productHandle = root.dataset.productHandle;
  let configuration;
  let lastQuote;
  let shippingQuote;
  let selectedShippingCode = '';

  const request = async (path, options = {}) => {
    const response = await fetch(`${apiBase}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {})
      }
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(payload.error || 'Something went wrong while loading this print configuration.');
    }

    return payload;
  };

  const money = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(Number(value));

  const getSelections = () => Object.fromEntries(
    [...root.querySelectorAll('[data-akd-option]')].map((field) => [field.dataset.akdOption, field.value])
  );

  const getShippingAddress = () => Object.fromEntries(
    [...root.querySelectorAll('[data-akd-shipping-address]')].map((field) => [field.dataset.akdShippingAddress, field.value.trim()])
  );

  const setStatus = (message, isError = false) => {
    const status = root.querySelector('[data-akd-status]');
    status.textContent = message || '';
    status.classList.toggle('akd-print-config__error', isError);
  };

  const render = () => {
    const fields = configuration.fields.map((field) => {
      const optionalChoice = field.optional
        ? '<option value="">No additional option</option>'
        : '';
      const values = optionalChoice + field.values.map((value) => (
        `<option value="${escapeHtml(value.id)}"${field.id === 'akd-source-choice' && value.id === configuration.product.sourceId ? ' selected' : ''}>${escapeHtml(value.name)}</option>`
      )).join('');

      return `
        <label class="akd-print-config__field">
          <span>${escapeHtml(field.label)}</span>
          <select data-akd-option="${escapeHtml(field.id)}" aria-label="${escapeHtml(field.label)}">${values}</select>
        </label>
      `;
    }).join('');

    root.innerHTML = `
      <section class="akd-print-config__builder" aria-label="Customize your order">
        <header class="akd-print-config__header">
          <p class="akd-print-config__eyebrow">Customize your order</p>
          <h2 class="akd-print-config__heading">Choose your print details</h2>
        </header>
        <div class="akd-print-config__layout">
          <div class="akd-print-config__main">
            <section class="akd-print-config__section" aria-labelledby="akd-print-specifications">
              <h3 id="akd-print-specifications" class="akd-print-config__section-title">Print specifications</h3>
              <div class="akd-print-config__grid">${fields}</div>
            </section>
            <section class="akd-print-config__section akd-print-config__artwork" aria-labelledby="akd-print-artwork">
              <div>
                <h3 id="akd-print-artwork" class="akd-print-config__section-title">Artwork</h3>
                <p class="akd-print-config__hint">PDF, JPG, PNG, or TIF up to 18 MB per file.</p>
              </div>
              <div class="akd-print-config__upload-grid">
                <label class="akd-print-config__upload" data-akd-upload="front">
                  <span class="akd-print-config__upload-label">Front artwork <em>Required</em></span>
                  <span class="akd-print-config__file-name" data-akd-file-name="front">Choose file</span>
                  <input data-akd-artwork="front" type="file" accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,application/pdf,image/jpeg,image/png,image/tiff" required>
                </label>
                <label class="akd-print-config__upload" data-akd-upload="back" data-akd-back-field>
                  <span class="akd-print-config__upload-label">Back artwork <em>Required when selected</em></span>
                  <span class="akd-print-config__file-name" data-akd-file-name="back">Choose file</span>
                  <input data-akd-artwork="back" type="file" accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,application/pdf,image/jpeg,image/png,image/tiff">
                </label>
              </div>
            </section>
            <section class="akd-print-config__section" aria-labelledby="akd-print-delivery">
              <div>
                <h3 id="akd-print-delivery" class="akd-print-config__section-title">Delivery</h3>
              </div>
              <div class="akd-print-config__grid">
                <label class="akd-print-config__field akd-print-config__field--wide">
                  <span>Address</span>
                  <input data-akd-shipping-address="address" type="text" autocomplete="shipping address-line1">
                </label>
                <label class="akd-print-config__field akd-print-config__field--wide">
                  <span>Apartment, suite, etc.</span>
                  <input data-akd-shipping-address="address2" type="text" autocomplete="shipping address-line2">
                </label>
                <label class="akd-print-config__field">
                  <span>City</span>
                  <input data-akd-shipping-address="city" type="text" autocomplete="shipping address-level2">
                </label>
                <label class="akd-print-config__field">
                  <span>State</span>
                  <input data-akd-shipping-address="state" type="text" autocomplete="shipping address-level1" maxlength="2">
                </label>
                <label class="akd-print-config__field">
                  <span>ZIP code</span>
                  <input data-akd-shipping-address="zipcode" type="text" autocomplete="shipping postal-code">
                </label>
                <label class="akd-print-config__field">
                  <span>Country</span>
                  <select data-akd-shipping-address="country" aria-label="Country">
                    <option value="US" selected>United States</option>
                    <option value="CA">Canada</option>
                  </select>
                </label>
              </div>
              <button class="akd-print-config__secondary-submit" type="button" data-akd-shipping-quote>Calculate shipping</button>
              <label class="akd-print-config__field" data-akd-shipping-method-field hidden>
                <span>Shipping method</span>
                <select data-akd-shipping-method aria-label="Shipping method"></select>
              </label>
            </section>
          </div>
          <aside class="akd-print-config__summary" aria-label="Order summary">
            <span class="akd-print-config__price-label">Item price</span>
            <strong class="akd-print-config__price" data-akd-price>Loading...</strong>
            <div class="akd-print-config__summary-line" data-akd-shipping-summary hidden>
              <span>Shipping</span>
              <strong data-akd-shipping-price></strong>
            </div>
            <div class="akd-print-config__summary-total">
              <span class="akd-print-config__price-label">Order total</span>
              <strong data-akd-total>Loading...</strong>
            </div>
            <p class="akd-print-config__status" data-akd-status role="status" aria-live="polite"></p>
            <button class="akd-print-config__submit" type="button" data-akd-checkout disabled>Continue to checkout</button>
          </aside>
        </div>
      </section>
    `;

    root.querySelectorAll('[data-akd-option]').forEach((field) => {
      field.addEventListener('change', field.dataset.akdOption === 'akd-source-choice'
        ? () => loadConfiguration(field.value)
        : refreshQuote);
    });
    root.querySelectorAll('[data-akd-artwork]').forEach((field) => field.addEventListener('change', updateArtworkLabel));
    root.querySelectorAll('[data-akd-shipping-address]').forEach((field) => field.addEventListener('input', resetShipping));
    const countryField = root.querySelector('[data-akd-shipping-address="country"]');
    countryField.addEventListener('change', resetShipping);
    root.querySelector('[data-akd-shipping-quote]').addEventListener('click', calculateShipping);
    root.querySelector('[data-akd-shipping-method]').addEventListener('change', selectShippingMethod);
    root.querySelector('[data-akd-checkout]').addEventListener('click', beginCheckout);
  };

  const updateArtworkLabel = (event) => {
    const input = event.currentTarget;
    const side = input.dataset.akdArtwork;
    const fileName = root.querySelector(`[data-akd-file-name="${side}"]`);
    const upload = root.querySelector(`[data-akd-upload="${side}"]`);
    const file = input.files?.[0];

    fileName.textContent = file ? file.name : 'Choose file';
    upload.classList.toggle('akd-print-config__upload--selected', Boolean(file));
  };

  const resetShipping = () => {
    shippingQuote = undefined;
    selectedShippingCode = '';
    const methodField = root.querySelector('[data-akd-shipping-method-field]');
    const methodSelect = root.querySelector('[data-akd-shipping-method]');
    const shippingSummary = root.querySelector('[data-akd-shipping-summary]');

    methodField.hidden = true;
    methodSelect.innerHTML = '';
    shippingSummary.hidden = true;
    root.querySelector('[data-akd-checkout]').disabled = true;
    updateOrderSummary();
  };

  const updateOrderSummary = () => {
    const itemPrice = Number(lastQuote?.price || 0);
    const shipping = shippingQuote?.methods?.find((method) => method.code === selectedShippingCode);
    const shippingSummary = root.querySelector('[data-akd-shipping-summary]');

    shippingSummary.hidden = !shipping;
    root.querySelector('[data-akd-shipping-price]').textContent = shipping ? shipping.formattedPrice : '';
    root.querySelector('[data-akd-total]').textContent = itemPrice
      ? money(itemPrice + Number(shipping?.price || 0))
      : 'Loading...';
  };

  const calculateShipping = async () => {
    const button = root.querySelector('[data-akd-shipping-quote]');
    const checkoutButton = root.querySelector('[data-akd-checkout]');

    if (!lastQuote) {
      await refreshQuote();
      if (!lastQuote) return;
    }

    button.disabled = true;
    checkoutButton.disabled = true;
    setStatus('Calculating live shipping...');

    try {
      shippingQuote = await request('/akd-storefront/shipping', {
        method: 'POST',
        body: JSON.stringify({
          product: productHandle,
          source: configuration.product.sourceId,
          selections: getSelections(),
          shipping: { address: getShippingAddress() }
        })
      });
      const methods = shippingQuote.methods || [];

      if (!methods.length) {
        throw new Error('No delivery services are available for that address.');
      }

      const methodField = root.querySelector('[data-akd-shipping-method-field]');
      const methodSelect = root.querySelector('[data-akd-shipping-method]');
      methodSelect.innerHTML = methods.map((method) => (
        `<option value="${escapeHtml(method.code)}">${escapeHtml(method.title)} - ${escapeHtml(method.formattedPrice)}</option>`
      )).join('');
      methodField.hidden = false;
      selectedShippingCode = methodSelect.value;
      updateOrderSummary();
      checkoutButton.disabled = false;
      setStatus('Shipping is ready for checkout.');
    } catch (error) {
      resetShipping();
      setStatus(error.message, true);
    } finally {
      button.disabled = false;
    }
  };

  const selectShippingMethod = (event) => {
    selectedShippingCode = event.currentTarget.value;
    updateOrderSummary();
    root.querySelector('[data-akd-checkout]').disabled = !selectedShippingCode;
  };

  const refreshQuote = async () => {
    const button = root.querySelector('[data-akd-checkout]');
    button.disabled = true;
    setStatus('Updating price...');

    try {
      lastQuote = await request('/akd-storefront/quote', {
        method: 'POST',
        body: JSON.stringify({
          product: productHandle,
          source: configuration.product.sourceId,
          selections: getSelections()
        })
      });
      root.querySelector('[data-akd-price]').textContent = lastQuote.formattedPrice || money(lastQuote.price);
      const backField = root.querySelector('[data-akd-back-field]');
      const backInput = root.querySelector('[data-akd-artwork="back"]');
      backField.hidden = !lastQuote.requiresBackArtwork;
      backInput.required = Boolean(lastQuote.requiresBackArtwork);
      resetShipping();
      setStatus(lastQuote.requiresBackArtwork ? 'Upload front and back artwork, then calculate shipping.' : 'Upload front artwork, then calculate shipping.');
    } catch (error) {
      root.querySelector('[data-akd-price]').textContent = 'Unavailable';
      setStatus(error.message, true);
    }
  };

  const loadConfiguration = async (sourceId = '') => {
    root.innerHTML = '<div class="akd-print-config__loading" role="status">Loading available options...</div>';

    try {
      const sourceQuery = sourceId ? `&source=${encodeURIComponent(sourceId)}` : '';
      configuration = await request(`/akd-storefront/config?product=${encodeURIComponent(productHandle)}${sourceQuery}`);
      render();
      await refreshQuote();
    } catch (error) {
      root.innerHTML = `<p class="akd-print-config__status akd-print-config__error" role="alert">${escapeHtml(error.message)}</p>`;
    }
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('That artwork file could not be read.'));
    reader.onload = () => resolve(String(reader.result).split(',').pop());
    reader.readAsDataURL(file);
  });

  const uploadArtwork = async (side) => {
    const input = root.querySelector(`[data-akd-artwork="${side}"]`);
    const file = input.files?.[0];

    if (!file) {
      throw new Error(`Please upload the ${side} artwork.`);
    }

    if (file.size > 18 * 1024 * 1024) {
      throw new Error(`${side[0].toUpperCase()}${side.slice(1)} artwork must be smaller than 18 MB.`);
    }

    return request('/akd-storefront/artwork', {
      method: 'POST',
      body: JSON.stringify({
        product: productHandle,
        side,
        name: file.name,
        mimeType: file.type || 'application/octet-stream',
        data: await fileToBase64(file)
      })
    });
  };

  const beginCheckout = async () => {
    const button = root.querySelector('[data-akd-checkout]');

    if (!lastQuote || !selectedShippingCode) {
      await refreshQuote();
      if (!lastQuote) return;
      setStatus('Calculate shipping and select a delivery option before checkout.', true);
      return;
    }

    button.disabled = true;
    setStatus('Saving artwork and preparing secure checkout...');

    try {
      const front = await uploadArtwork('front');
      const back = lastQuote.requiresBackArtwork ? await uploadArtwork('back') : null;
      const checkout = await request('/akd-storefront/checkout', {
        method: 'POST',
        body: JSON.stringify({
          product: productHandle,
          source: configuration.product.sourceId,
          selections: getSelections(),
          artwork: { front: front.token, back: back?.token || '' },
          shipping: {
            address: getShippingAddress(),
            serviceCode: selectedShippingCode
          },
          requestId: crypto.randomUUID()
        })
      });
      window.location.assign(checkout.checkoutUrl);
    } catch (error) {
      setStatus(error.message, true);
      button.disabled = false;
    }
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  loadConfiguration();
})();
